import React from "react";
import { Church as ChurchIcon, MapPin, Clock, Target, Eye, User } from "lucide-react";
import { useFetch } from "@/lib/api";
import { Reveal } from "@/components/blocks/Reveal";
import { SectionHeading } from "@/components/blocks/SectionHeading";
import { EmptyState } from "@/components/blocks/EmptyState";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { RichTextContent } from "@/components/blocks/RichTextContent";

const InfoRow = ({ icon: Icon, label, value, placeholder }) => (
  <div className="flex gap-4 border-b border-border py-5">
    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-secondary text-brand-orange">
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <p className="font-display text-sm font-semibold uppercase tracking-wider text-navy">{label}</p>
      <p className="mt-1 text-sm text-muted-foreground">{value || <span className="italic text-muted-foreground/70">{placeholder}</span>}</p>
    </div>
  </div>
);

const Church = () => {
  const { data: c } = useFetch("/church");
  if (!c) return <div className="min-h-screen" />;

  return (
    <div>
      {/* Distinct light "Swiss" hero for the church */}
      <section className="relative bg-white pt-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-8">
          <Reveal>
            <span className="inline-flex items-center gap-2 overline text-brand-orange">
              <ChurchIcon className="h-4 w-4" /> A Church Under Flames of Fire
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-navy sm:text-5xl lg:text-6xl">
              {c.name}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">{c.tagline}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <img src={c.image} alt={c.name} className="w-full object-cover" style={{ aspectRatio: "4/3" }} />
          </Reveal>
        </div>
      </section>

      {/* About + Mission/Vision */}
      <section className="bg-secondary/40 py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-20">
            <div>
              <SectionHeading overline="About The Church" title="A family being discipled" />
              <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">{c.about}</p>
            </div>
            <div className="space-y-6">
              <div className="border border-border bg-card p-8">
                <Target className="h-8 w-8 text-brand-orange" />
                <h3 className="mt-4 font-display text-lg font-semibold text-navy">Mission</h3>
                {c.mission ? (
                  <RichTextContent
                    className="mt-2 text-sm leading-relaxed text-muted-foreground"
                    html={c.mission}
                    testId="church-mission-rich-text"
                  />
                ) : (
                  <p className="mt-2 text-sm italic text-muted-foreground/70">
                    {c.mission_placeholder}
                  </p>
                )}
              </div>
              <div className="border border-border bg-card p-8">
                <Eye className="h-8 w-8 text-brand-orange" />
                <h3 className="mt-4 font-display text-lg font-semibold text-navy">Vision</h3>
                {c.vision ? (
                  <RichTextContent
                    className="mt-2 text-sm leading-relaxed text-muted-foreground"
                    html={c.vision}
                    testId="church-vision-rich-text"
                  />
                ) : (
                  <p className="mt-2 text-sm italic text-muted-foreground/70">
                    {c.vision_placeholder}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service times + Location + Leadership */}
      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-20">
            <div>
              <SectionHeading overline="Plan A Visit" title="Service Times & Location" />
              <div className="mt-8">
                <InfoRow icon={Clock} label="Service Times" value={c.service_times?.length ? c.service_times.join(" · ") : null} placeholder={c.service_times_placeholder} />
                <InfoRow icon={MapPin} label="Location" value={c.location || null} placeholder={c.location_placeholder} />
              </div>
            </div>
            <div>
              <SectionHeading overline="Church Leadership" title="Those who shepherd" />
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {(c.leadership || []).map((l, i) => (
                  <div key={i} className="border border-border bg-card p-6 text-center">
                    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-navy text-white/40">
                      {l.image ? <img src={l.image} alt={l.name} className="h-full w-full rounded-full object-cover" /> : <User className="h-7 w-7" />}
                    </span>
                    <h3 className="mt-4 font-display text-base font-semibold text-navy">{l.name}</h3>
                    <p className="text-sm text-brand-orange">{l.role}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Departments */}
      <section className="bg-navy py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading dark overline="Church Departments" title="Serving together" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {(c.departments || []).map((d, i) => (
              <div key={i} className="border border-white/10 bg-white/5 p-7">
                <h3 className="font-display text-lg font-semibold text-white">{d.name}</h3>
                <p className="mt-2 text-sm text-white/70">{d.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Church media placeholders */}
      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-8 md:grid-cols-3">
            <div>
              <h3 className="font-display text-xl font-semibold text-navy">Sermons</h3>
              <div className="mt-4"><EmptyState title="Coming soon" /></div>
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-navy">Announcements</h3>
              <div className="mt-4"><EmptyState title="Coming soon" /></div>
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-navy">Gallery</h3>
              <div className="mt-4"><EmptyState title="Coming soon" /></div>
            </div>
          </div>
          <div className="mt-12">
            <Button asChild className="rounded-sm bg-brand-orange font-display uppercase tracking-wide hover:bg-brand-orange-600">
              <Link to="/contact" data-testid="church-contact-link">Contact The Church</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Church;
